import * as R from 'ramda';
import { v4 as uuid } from 'uuid';
import { toBinary } from '@trs/utils';
import { RULES_KEYS, getNewRule } from './reducer/initialState';

const TEXT_ONLY_FORMULA_TYPE = 'text-only';

const encodeValue = (value) =>
  typeof value === 'boolean' ? toBinary(value.toString()) : toBinary(value);

const isGroup = (rule) => Array.isArray(R.prop('eligibilityRows', rule));

const mapEligibilityRow = (rule, findLookup) => {
  if (isGroup(rule)) {
    return {
      eligibilityRows: rule.eligibilityRows.map((row) => mapEligibilityRow(row, findLookup)),
      eligibilityRowsOperator: rule.eligibilityRowsOperator,
    };
  }

  return {
    field: rule.variable,
    operator: rule.operator,
    values: (rule.values || []).map(encodeValue),
    isLookup: findLookup(rule.variable),
  };
};

// server expects a rows/rowsOperator wrapper even for a single, ungrouped rule
export const mapEligibility = (eligibility, findLookup) => {
  if (R.isEmpty(eligibility) || R.isNil(eligibility)) {
    return { eligibilityRows: [], eligibilityRowsOperator: '' };
  }

  if (isGroup(eligibility)) {
    return {
      eligibilityRows: eligibility.eligibilityRows.map((row) =>
        mapEligibilityRow(row, findLookup)
      ),
      eligibilityRowsOperator: eligibility.eligibilityRowsOperator,
    };
  }

  return {
    eligibilityRows: [mapEligibilityRow(eligibility, findLookup)],
    eligibilityRowsOperator: '',
  };
};

const mapFormulaItem = (item) => {
  if (R.isEmpty(item) || R.isNil(item)) return item;

  const value =
    R.prop('type', item) === TEXT_ONLY_FORMULA_TYPE
      ? toBinary(R.prop('value', item))
      : R.prop('value', item);

  const mapped = { type: R.prop('type', item), value };

  // R.isEmpty(undefined) is false, so guard explicitly against a missing arguments array
  if (Array.isArray(item.arguments) && item.arguments.length > 0) {
    mapped.arguments = item.arguments.map((argGroup) => (argGroup || []).map(mapFormulaItem));
  }

  return mapped;
};

export const mapFormula = (formula) => (formula || []).map(mapFormulaItem);

const findLookupVariable = (systemVariables) => (variable) => {
  const systemVariable = (systemVariables || []).find(R.propEq('value', variable));
  return !!R.prop('valuesUrl', systemVariable);
};

const mapPopulationToRequestModel = (population, findLookup) => ({
  id: String(population.id ?? ''),
  name: population.name,
  eligibility: mapEligibility(population.eligibility, findLookup),
});

const mapRuleToRequestModel = (rule, index, findLookup) => ({
  id: rule.key,
  name: `rule-${index}`,
  label: rule.label || '',
  eligibility: mapEligibility(rule.eligibility, findLookup),
  formula: { items: mapFormula(rule.formula) },
});

export const mapBonusModellerPayload = (bonusModeller) => {
  const { populations, includeDisclaimer, disclaimer, systemVariables } = bonusModeller;
  const findLookup = findLookupVariable(systemVariables);

  return {
    eligibilityRules: (populations || []).map((population) =>
      mapPopulationToRequestModel(population, findLookup)
    ),
    disclaimer: {
      included: includeDisclaimer,
      content: includeDisclaimer ? toBinary(disclaimer) : null,
    },
    bonusCompensationRules: (bonusModeller[RULES_KEYS.compensation] || []).map((rule, index) =>
      mapRuleToRequestModel(rule, index, findLookup)
    ),
    bonusMidpointRules: (bonusModeller[RULES_KEYS.midpoint] || []).map((rule, index) =>
      mapRuleToRequestModel(rule, index, findLookup)
    ),
  };
};


const reverseEligibilityRow = (row) => {
  // Only treat as a group if it has eligibilityRows AND it's not null/empty
  if (Array.isArray(row.eligibilityRows) && row.eligibilityRows.length > 0) { 
    return {
      uid: uuid(),
      eligibilityRows: row.eligibilityRows.map(reverseEligibilityRow),
      eligibilityRowsOperator: row.eligibilityRowsOperator,
    };
  }

  // Simple rule - strip out any null group properties that may have come from the server
  return {
    uid: uuid(),
    variable: row.field,
    operator: row.operator,
    values: row.values || [],
  };
};

// an empty object keeps the builder in its "start building" placeholder state
export const reverseEligibility = (eligibility) => {
  const rows = R.propOr([], 'eligibilityRows', eligibility);

  if (R.isEmpty(rows)) return {};

  // the builder always expects a group at the root, even for a single rule
  return {
    uid: uuid(),
    eligibilityRows: rows.map(reverseEligibilityRow),
    eligibilityRowsOperator: R.propOr('', 'eligibilityRowsOperator', eligibility),
  };
};

const reverseFormulaItem = (item) => {
  if (R.isEmpty(item) || R.isNil(item)) return item;

  const mapped = {
    key: uuid(),
    type: R.prop('type', item),
    value: R.prop('value', item),
    displayName: '',
    description: '',
  };

  if (Array.isArray(item.arguments) && item.arguments.length > 0) {
    mapped.arguments = item.arguments.map((argGroup) => (argGroup || []).map(reverseFormulaItem));
  }

  return mapped;
};

export const reverseFormula = (items) => (items || []).map(reverseFormulaItem);

const reverseRule = (rule) => ({
  key: rule.id || uuid(),
  eligibility: reverseEligibility(rule.eligibility),
  formula: reverseFormula(R.path(['formula', 'items'], rule)),
  localOverride: {},
});

// each step always renders at least one rule block, even when nothing is saved yet
const reverseRules = (rules) => {
  const mapped = (rules || []).map(reverseRule);
  return mapped.length > 0 ? mapped : [getNewRule()];
};

const reversePopulation = (population, index) => ({
  key: index,
  id: population.id,
  name: population.name,
  eligibility: reverseEligibility(population.eligibility),
  localOverride: {},
});

export const mapBonusModellerResponse = (response) => {
  const data = response || {};
  
  const populations = Array.isArray(data.eligiblePopulationRules)
    ? data.eligiblePopulationRules.map(reversePopulation)
    : [];

  // If no populations exist, create a default one for the eligible population step
  const finalPopulations =
    populations.length > 0
      ? populations
      : [
          {
            key: 0,
            id: 0,
            name: 'Rule0',
            eligibility: {},
            localOverride: {},
          },
        ];

  return {
    populations: finalPopulations,
    includeDisclaimer: !!R.path(['disclaimer', 'included'], data),
    disclaimer: R.path(['disclaimer', 'content'], data) || '',
    [RULES_KEYS.compensation]: reverseRules(data.bonusCompensationRules),
    [RULES_KEYS.midpoint]: reverseRules(data.bonusMidpointRules),
    status: R.prop('status', data) || '',
  };
};

export default mapBonusModellerPayload;
